import React, { useRef, useState, useMemo } from 'react';
import { ResponsiveBar } from '@nivo/bar';
import { FaSlidersH, FaTimes, FaTint } from 'react-icons/fa';
import ChartCard, { ChartCardIconButton, ChartCardButton } from '../shared/ChartCard.js';
import SegmentedToggle from '../shared/SegmentedToggle.js';
import { buildExportSvg, downloadSvg } from '../shared/svgExport.js';
import { FONT_STACK, DARK_TEXT, MUTED_TEXT, withHexAlpha } from '../shared/chartTheme.js';
import '../../styles/DashboardComponents.css';

// Apparence fixe du mode Interactif ; le mode Export pilote les mêmes champs via
// `settings`. Bleu/rouge par défaut (au lieu d'une teinte par usage) — deux séries
// fixes (coût de l'eau / coût du chauffage), pas une catégorie par couleur.
const INTERACTIVE_APPEARANCE = {
    coldColor: '#205673',
    heatingColor: '#fe9273',
    padding: 0.3,
    cornerRadius: 0,
    fontSize: 12,
    labelFontSize: 12, // montants en € au bout des barres
    rowHeight: 34,
    chartWidth: 560,
    roundTo: 10, // arrondi des montants affichés (€) : 0.01, 1, 10 ou 100
};

const DEFAULT_SETTINGS = { ...INTERACTIVE_APPEARANCE };

// Marge haute réservée au total (TotalLayer) : deux lignes de texte + un peu d'air
// avant la première barre.
const TOP_MARGIN = 56;

const SETTINGS_SECTIONS = [
    {
        id: 'dimensions',
        title: 'Dimensions',
        fields: [
            { key: 'chartWidth', label: 'Largeur du graphique', type: 'range', min: 320, max: 900, step: 20, unit: 'px' },
            { key: 'rowHeight', label: 'Hauteur des lignes', type: 'range', min: 24, max: 48, step: 2, unit: 'px' },
        ],
    },
    {
        id: 'colors',
        title: 'Couleurs',
        fields: [
            { key: 'coldColor', label: 'Couleur eau froide', type: 'color' },
            { key: 'heatingColor', label: 'Couleur chauffage', type: 'color' },
        ],
    },
    {
        id: 'bars',
        title: 'Barres',
        fields: [
            { key: 'padding', label: 'Espacement entre les barres', type: 'range', min: 0.1, max: 0.8, step: 0.05 },
            { key: 'cornerRadius', label: 'Arrondi des coins', type: 'range', min: 0, max: 20, step: 1, unit: 'px' },
        ],
    },
    {
        id: 'typography',
        title: 'Typographie',
        fields: [
            { key: 'fontSize', label: 'Taille de police', type: 'range', min: 10, max: 20, step: 1, unit: 'px' },
            { key: 'labelFontSize', label: 'Taille des montants', type: 'range', min: 9, max: 24, step: 1, unit: 'px' },
        ],
    },
    {
        id: 'values',
        title: 'Valeurs',
        fields: [
            {
                key: 'roundTo',
                label: 'Arrondi des montants',
                type: 'select',
                options: [
                    { value: 0.01, label: 'Au centime' },
                    { value: 1, label: "À l'euro" },
                    { value: 10, label: 'À la dizaine' },
                    { value: 100, label: 'À la centaine' },
                ],
            },
        ],
    },
];

const SettingsField = ({ field, settings, onChange }) => (
    <div className="sunburst-settings-row" key={field.key}>
        <label htmlFor={`cost-chart-setting-${field.key}`}>{field.label}</label>
        {field.type === 'range' ? (
            <div className="sunburst-settings-range">
                <input
                    id={`cost-chart-setting-${field.key}`}
                    type="range"
                    min={field.min}
                    max={field.max}
                    step={field.step}
                    value={settings[field.key]}
                    onChange={(e) => onChange(field.key, Number(e.target.value))}
                />
                <span className="sunburst-settings-value">
                    {settings[field.key]}
                    {field.unit}
                </span>
            </div>
        ) : field.type === 'select' ? (
            <select
                id={`cost-chart-setting-${field.key}`}
                className="sunburst-settings-select"
                value={settings[field.key]}
                onChange={(e) => onChange(field.key, Number(e.target.value))}
            >
                {field.options.map((option) => (
                    <option key={option.value} value={option.value}>
                        {option.label}
                    </option>
                ))}
            </select>
        ) : field.type === 'color' ? (
            <input
                id={`cost-chart-setting-${field.key}`}
                type="color"
                className="sunburst-settings-color"
                value={settings[field.key]}
                onChange={(e) => onChange(field.key, e.target.value)}
            />
        ) : (
            <label className="sunburst-settings-toggle">
                <input
                    id={`cost-chart-setting-${field.key}`}
                    type="checkbox"
                    checked={settings[field.key]}
                    onChange={(e) => onChange(field.key, e.target.checked)}
                />
                <span className="sunburst-settings-toggle-slider" />
            </label>
        )}
    </div>
);

const SettingsPanel = ({ settings, onChange, onClose }) => (
    <div className="sunburst-settings-panel">
        <div className="sunburst-settings-panel-header">
            <span>Réglages d'affichage</span>
            <button type="button" className="sunburst-settings-close" onClick={onClose} aria-label="Fermer">
                <FaTimes />
            </button>
        </div>
        <div className="sunburst-settings-grid">
            {SETTINGS_SECTIONS.map((section) => (
                <div className="sunburst-settings-section" key={section.id}>
                    <div className="sunburst-settings-section-title">{section.title}</div>
                    {section.fields.map((field) => (
                        <SettingsField key={field.key} field={field} settings={settings} onChange={onChange} />
                    ))}
                </div>
            ))}
        </div>
    </div>
);

const formatPrice = (value) => (value ?? 0).toFixed(2).replace('.', ',');

// `roundTo` : pas d'arrondi en euros (0.01 = au centime, 10 = à la dizaine…) ; les
// centimes ne sont affichés que pour un pas inférieur à l'euro.
const formatCurrency = (value, roundTo = 0.01) => {
    const decimals = roundTo < 1 ? 2 : 0;
    const rounded = Math.round((value ?? 0) / roundTo) * roundTo;
    const [intPart, decPart] = rounded.toFixed(decimals).split('.');
    const grouped = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
    return decPart ? `${grouped},${decPart} €` : `${grouped} €`;
};

// Montant total au bout de chaque barre empilée. Remplace le layer natif 'totals' de
// Nivo, dont les <text> portent un style invalide (`outline-opacity:1px`) et
// `alignment-baseline` : Canva refusait d'importer le SVG exporté.
const BarTotalsLayer = ({ bars, appearance }) => {
    const rows = new Map();
    bars.forEach((bar) => {
        const key = bar.data.indexValue;
        const row = rows.get(key) || { right: 0, y: bar.y + bar.height / 2, total: bar.data.data.total };
        row.right = Math.max(row.right, bar.x + bar.width);
        rows.set(key, row);
    });
    return (
        <g>
            {Array.from(rows, ([key, row]) => (
                <text
                    key={key}
                    x={row.right + 10}
                    y={row.y}
                    dominantBaseline="central"
                    style={{ fontSize: appearance.labelFontSize, fontWeight: 700, fontFamily: FONT_STACK, fill: DARK_TEXT }}
                >
                    {formatCurrency(row.total, appearance.roundTo)}
                </text>
            ))}
        </g>
    );
};

// Total (somme des deux séries, toutes les barres confondues), en <text> SVG — comme
// le total central des donuts/sunbursts et de "Consommation par personne", capturé par
// l'export SVG (buildExportSvg ne clone que le <svg> lui-même).
// Nivo rend chaque layer (natif ou custom) à l'intérieur d'un <g> déjà translaté de
// (margin.left, margin.top) : les coordonnées ci-dessous sont donc LOCALES à ce repère,
// pas absolues au canevas SVG — d'où le -margin.top pour remonter dans la marge haute
// réservée (sinon le total atterrit à la hauteur de la 1ère barre, cf. incident vécu).
const TotalLayer = ({ width, margin, total, appearance }) => {
    const centerX = (width - margin.left - margin.right) / 2;
    const topY = -margin.top;
    const valueSize = Math.max(16, appearance.fontSize + 5);
    return (
        <g style={{ pointerEvents: 'none' }}>
            <text
                x={centerX}
                y={topY + 18}
                textAnchor="middle"
                dominantBaseline="central"
                style={{ fontSize: valueSize, fontWeight: 700, fontFamily: FONT_STACK, fill: DARK_TEXT }}
            >
                {formatCurrency(total, appearance.roundTo)}
            </text>
            <text
                x={centerX}
                y={topY + 36}
                textAnchor="middle"
                dominantBaseline="central"
                style={{ fontSize: 10, fontWeight: 600, fontFamily: FONT_STACK, fill: MUTED_TEXT, letterSpacing: '0.03em' }}
            >
                TOTAL
            </text>
        </g>
    );
};

// Synthèse des tarifs unitaires (façon fiche eau froide/eau chaude) — HTML/CSS, pas
// intégrée au SVG exporté (contrairement au total du graphique) : ce n'est pas un
// contenu du graphique lui-même, juste un rappel de contexte à côté.
const PriceSynthesis = ({ coldPrice, hotPrice, coldColor, heatingColor }) => (
    <div className="cost-synthesis">
        <div className="cost-synthesis-item">
            <div className="cost-synthesis-icon-wrap" style={{ background: withHexAlpha(coldColor, 0.15) }}>
                <FaTint className="cost-synthesis-icon" style={{ color: coldColor }} />
            </div>
            <div className="cost-synthesis-label">1m³ d'eau froide</div>
            <div className="cost-synthesis-price">{formatPrice(coldPrice)} €</div>
        </div>
        <div className="cost-synthesis-item">
            <div className="cost-synthesis-icon-wrap" style={{ background: withHexAlpha(heatingColor, 0.15) }}>
                <FaTint className="cost-synthesis-icon" style={{ color: heatingColor }} />
            </div>
            <div className="cost-synthesis-label">1m³ d'eau chaude</div>
            <div className="cost-synthesis-price">{formatPrice(hotPrice)} €</div>
        </div>
    </div>
);

/* ─────────────────────────────────────────────
   "Coût par usage" — même patron Interactif/Export que les autres graphiques,
   entièrement autonome (mode, répartition usage/sous-usage, réglages d'apparence
   restent internes). Contrairement aux 3 précédents, la donnée n'est pas un simple
   volume agrégé : chaque ligne de `waterPointRows` porte son propre ratio eau chaude,
   donc l'agrégation catégorie/sous-catégorie (volume × ratio × prix) se fait ici,
   directement depuis les points d'eau enrichis plutôt que depuis des totaux déjà
   sommés.

   Décomposition reprise à l'identique de l'ancien calcul back (cost_per_use) : la
   barre empile "coût de l'eau" (= tout le volume au tarif froid) + "coût du
   chauffage" (= surcoût dû à la part chauffée), pas "coût réel froid" + "coût réel
   chaud" — cf. légende/synthèse eau froide vs chauffage.

   @param {object[]} waterPointRows - enrichi (usage_category_label/usage_subcategory_label/hot_water_ratio_effective)
   @param {number} coldPrice - €/m³ eau froide (global_water_data.water_price)
   @param {number} hotPrice - €/m³ eau chaude (global_water_data.hot_water_price)
───────────────────────────────────────────── */
const CostPerUsageCard = ({ waterPointRows, coldPrice, hotPrice }) => {
    const containerRef = useRef(null);
    const [mode, setMode] = useState('interactive'); // 'interactive' | 'export'
    const [filter, setFilter] = useState('usage'); // 'usage' | 'subcategory'
    const [settings, setSettings] = useState(DEFAULT_SETTINGS);
    const [panelOpen, setPanelOpen] = useState(false);
    const [downloadState, setDownloadState] = useState('idle');

    const updateSetting = (key, value) => setSettings((prev) => ({ ...prev, [key]: value }));
    const appearance = mode === 'export' ? settings : INTERACTIVE_APPEARANCE;

    const safeColdPrice = coldPrice || 0;
    const safeHotPrice = hotPrice ?? safeColdPrice;
    const labelField = filter === 'usage' ? 'usage_category_label' : 'usage_subcategory_label';

    // Agrège volume × ratio eau chaude × prix par catégorie/sous-catégorie, à partir
    // des points d'eau enrichis (chaque ligne = un usage, avec son propre ratio). Les
    // libellés (et leur désambiguïsation "Autres (Catégorie)") sont déjà résolus en
    // amont (ClientBuildingDashboard.js) — pas de recalcul de ce côté.
    const chartData = useMemo(() => {
        const byLabel = new Map();
        (waterPointRows || []).forEach((row) => {
            const label = row[labelField];
            if (!label) return;
            const volume = row.annual_volume || 0;
            const hotRatio = row.hot_water_ratio_effective || 0;
            const entry = byLabel.get(label) || { label, cold: 0, heating: 0 };
            entry.cold += volume * safeColdPrice;
            entry.heating += volume * hotRatio * (safeHotPrice - safeColdPrice);
            byLabel.set(label, entry);
        });
        return Array.from(byLabel.values())
            .map((e) => ({ ...e, total: e.cold + e.heating }))
            // croissant : Nivo (bar horizontal) rend le 1er élément du tableau en bas
            // et le dernier en haut — donc le plus cher doit être le dernier ici.
            .sort((a, b) => a.total - b.total);
    }, [waterPointRows, labelField, safeColdPrice, safeHotPrice]);

    const rowHeight = appearance.rowHeight;
    const totalValue = chartData.reduce((sum, d) => sum + d.total, 0);
    const chartHeight = chartData.length * rowHeight + TOP_MARGIN;

    const handleDownloadSvg = () => {
        const svgEl = containerRef.current?.querySelector('svg');
        if (!svgEl) return;
        downloadSvg(buildExportSvg(svgEl), 'cout-par-usage.svg');
        setDownloadState('downloaded');
        setTimeout(() => setDownloadState('idle'), 2000);
    };

    const modeToggle = (
        <SegmentedToggle
            ariaLabel="Mode d'affichage du graphique"
            value={mode}
            onChange={setMode}
            options={[
                { value: 'interactive', label: 'Interactif' },
                { value: 'export', label: 'Export' },
            ]}
        />
    );

    return (
        <ChartCard
            title="Coût par usage"
            actions={
                mode === 'export' ? (
                    <>
                        {modeToggle}
                        <ChartCardIconButton
                            onClick={() => setPanelOpen((v) => !v)}
                            title="Réglages d'affichage"
                            aria-label="Réglages d'affichage"
                        >
                            <FaSlidersH />
                        </ChartCardIconButton>
                        <ChartCardButton onClick={handleDownloadSvg} title="Télécharger le graphique en SVG">
                            {downloadState === 'downloaded' ? 'SVG téléchargé' : 'Télécharger en SVG'}
                        </ChartCardButton>
                    </>
                ) : (
                    modeToggle
                )
            }
            panel={mode === 'export' && panelOpen && <SettingsPanel settings={settings} onChange={updateSetting} onClose={() => setPanelOpen(false)} />}
        >
            <div className="chart-settings-bar">
                <div className="chart-settings-bar-group">
                    <p>Répartir par</p>
                    <SegmentedToggle
                        variant="primary"
                        ariaLabel="Répartir par"
                        value={filter}
                        onChange={setFilter}
                        options={[
                            { value: 'usage', label: 'Usage' },
                            { value: 'subcategory', label: 'Sous-usage' },
                        ]}
                    />
                </div>
            </div>

            <div className="sunburst-card-body">
                {chartData.length > 0 && (
                    <div className="consumption-gauge-container" ref={containerRef} style={{ width: appearance.chartWidth, height: chartHeight }}>
                        <ResponsiveBar
                            data={chartData}
                            keys={['cold', 'heating']}
                            indexBy="label"
                            layout="horizontal"
                            groupMode="stacked"
                            margin={{ top: TOP_MARGIN, right: 100, bottom: 0, left: 140 }}
                            padding={appearance.padding}
                            colors={(bar) => (bar.id === 'cold' ? appearance.coldColor : appearance.heatingColor)}
                            borderRadius={appearance.cornerRadius}
                            enableGridX={false}
                            enableGridY={false}
                            axisBottom={null}
                            axisLeft={{ tickSize: 0, tickPadding: 8 }}
                            enableLabel={false}
                            isInteractive={true}
                            animate={false}
                            valueFormat={(v) => formatCurrency(v, appearance.roundTo)}
                            layers={['grid', 'axes', 'bars', (p) => <BarTotalsLayer {...p} appearance={appearance} />, (p) => <TotalLayer {...p} total={totalValue} appearance={appearance} />, 'markers', 'legends', 'annotations']}
                            theme={{
                                text: { fontFamily: FONT_STACK },
                                axis: { ticks: { text: { fontSize: appearance.fontSize, fill: DARK_TEXT, fontFamily: FONT_STACK } } },
                            }}
                            tooltip={({ id, value, color, indexValue }) => (
                                <div className="sunburst-tooltip">
                                    <div className="sunburst-tooltip-label">{indexValue}</div>
                                    <div className="sunburst-tooltip-value">
                                        <span
                                            style={{
                                                display: 'inline-block',
                                                width: 8,
                                                height: 8,
                                                borderRadius: 2,
                                                background: color,
                                                marginRight: 6,
                                            }}
                                        />
                                        {id === 'cold' ? "Coût de l'eau" : 'Coût du chauffage'} : {formatCurrency(value, appearance.roundTo)}
                                    </div>
                                </div>
                            )}
                        />
                    </div>
                )}

                <PriceSynthesis
                    coldPrice={safeColdPrice}
                    hotPrice={safeHotPrice}
                    coldColor={appearance.coldColor}
                    heatingColor={appearance.heatingColor}
                />
            </div>
        </ChartCard>
    );
};

export default CostPerUsageCard;
